import type { Enums } from '@repo/supabase-client';
import { supabase } from '@/lib/supabase';
import { getTodayKey, dateRangeForDay } from '@/lib/tz';
import { scalarFromLinks, type LinkEntry } from '@/lib/links';

// Embeds `source_review_item:review_items!source_review_item_id(email:emails!email_id(subject))`
// so Today/Schedule cards can render a "From: {subject}" chip without a
// second round-trip. The join costs nothing when the row has no
// source_review_item_id (null FK just yields null embed).
//
// EVENT_SELECT additionally pulls `is_time_unknown` off that same join:
// it lives on `review_items` and is not copied onto `events`, so it is the
// only way a committed event can still tell a real noon from an extraction
// that never had an hour (YAR-391).
const TASK_SELECT =
  '*, task_children(child_id, children(id, name, emoji_avatar, emoji_color, avatar_image_path)), task_members(user_id, users(id, display_name, email, avatar_emoji, avatar_color, avatar_image_path)), source_review_item:review_items!source_review_item_id(email:emails!email_id(subject))';
const EVENT_SELECT =
  '*, event_children(child_id, children(id, name, emoji_avatar, emoji_color, avatar_image_path)), event_members(user_id, users(id, display_name, email, avatar_emoji, avatar_color, avatar_image_path)), source_review_item:review_items!source_review_item_id(is_time_unknown, email:emails!email_id(subject))';

/**
 * Apply the desired child list to a task by diffing against the current
 * rows, NOT by delete-all + insert-all. The difference matters because
 * every insert into `task_children` fires the `notify_task_assigned`
 * trigger; a naive delete-then-insert replay notifies the household
 * about every child on every save, even when the assignment didn't
 * change. We only INSERT children that weren't already assigned and
 * only DELETE children that were removed.
 */
async function replaceTaskChildren(taskId: string, childIds: string[]) {
  const desired = new Set(childIds);
  const { data: current, error: readError } = await supabase
    .from('task_children')
    .select('child_id')
    .eq('task_id', taskId);
  if (readError) throw readError;

  const existing = new Set((current ?? []).map((row) => row.child_id));
  const toInsert = childIds.filter((id) => !existing.has(id));
  const toDelete = Array.from(existing).filter((id) => !desired.has(id));

  if (toDelete.length > 0) {
    const { error: delError } = await supabase
      .from('task_children')
      .delete()
      .eq('task_id', taskId)
      .in('child_id', toDelete);
    if (delError) throw delError;
  }

  if (toInsert.length > 0) {
    const { error: insError } = await supabase
      .from('task_children')
      .insert(toInsert.map((child_id) => ({ task_id: taskId, child_id })));
    if (insError) throw insError;
  }
}

/**
 * Same diff strategy as replaceTaskChildren but for the user-level
 * (`task_members`) join. Avoids re-firing notify_task_member_assigned
 * for users who were already on the task.
 */
async function replaceTaskMembers(taskId: string, userIds: string[]) {
  const desired = new Set(userIds);
  const { data: current, error: readError } = await supabase
    .from('task_members')
    .select('user_id')
    .eq('task_id', taskId);
  if (readError) throw readError;

  const existing = new Set((current ?? []).map((row) => row.user_id));
  const toInsert = userIds.filter((id) => !existing.has(id));
  const toDelete = Array.from(existing).filter((id) => !desired.has(id));

  if (toDelete.length > 0) {
    const { error: delError } = await supabase
      .from('task_members')
      .delete()
      .eq('task_id', taskId)
      .in('user_id', toDelete);
    if (delError) throw delError;
  }

  if (toInsert.length > 0) {
    const { error: insError } = await supabase
      .from('task_members')
      .insert(toInsert.map((user_id) => ({ task_id: taskId, user_id })));
    if (insError) throw insError;
  }
}

async function replaceEventMembers(eventId: string, userIds: string[]) {
  const desired = new Set(userIds);
  const { data: current, error: readError } = await supabase
    .from('event_members')
    .select('user_id')
    .eq('event_id', eventId);
  if (readError) throw readError;

  const existing = new Set((current ?? []).map((row) => row.user_id));
  const toInsert = userIds.filter((id) => !existing.has(id));
  const toDelete = Array.from(existing).filter((id) => !desired.has(id));

  if (toDelete.length > 0) {
    const { error: delError } = await supabase
      .from('event_members')
      .delete()
      .eq('event_id', eventId)
      .in('user_id', toDelete);
    if (delError) throw delError;
  }

  if (toInsert.length > 0) {
    const { error: insError } = await supabase
      .from('event_members')
      .insert(toInsert.map((user_id) => ({ event_id: eventId, user_id })));
    if (insError) throw insError;
  }
}

/**
 * Same diff strategy as replaceTaskChildren — see that function's doc
 * comment for why. The trigger being avoided is
 * notify_event_collaborator_added.
 */
async function replaceEventChildren(eventId: string, childIds: string[]) {
  const desired = new Set(childIds);
  const { data: current, error: readError } = await supabase
    .from('event_children')
    .select('child_id')
    .eq('event_id', eventId);
  if (readError) throw readError;

  const existing = new Set((current ?? []).map((row) => row.child_id));
  const toInsert = childIds.filter((id) => !existing.has(id));
  const toDelete = Array.from(existing).filter((id) => !desired.has(id));

  if (toDelete.length > 0) {
    const { error: delError } = await supabase
      .from('event_children')
      .delete()
      .eq('event_id', eventId)
      .in('child_id', toDelete);
    if (delError) throw delError;
  }

  if (toInsert.length > 0) {
    const { error: insError } = await supabase
      .from('event_children')
      .insert(toInsert.map((child_id) => ({ event_id: eventId, child_id })));
    if (insError) throw insError;
  }
}

/**
 * "Delete this and following" matched no rows — most likely RLS refused the
 * caller silently (YAR-443). Surfaced as a failure, never as success.
 */
export class SeriesDeleteNothingDeletedError extends Error {
  constructor() {
    super('No sessions were deleted');
    this.name = 'SeriesDeleteNothingDeletedError';
  }
}

export const scheduleService = {
  /*
   * Every read below binds `household_id` to the ACTIVE household.
   *
   * RLS on `tasks`/`events` is `household_id = ANY(my_household_ids())` — every
   * household the caller belongs to. That is the right authorization boundary
   * and the wrong scope: it answers "what may I see?", not "what am I looking
   * at?". While every user had exactly one household the two were identical,
   * which is why this went unnoticed to production. For a parent who owns one
   * household and is an Editor in another it served the UNION of both on
   * Today, Schedule and History — two children called Isaac from two different
   * families in one filter bar — and made the household switcher cosmetic.
   *
   * Pinned by `__tests__/schedule.service.household-scope.test.ts`.
   */
  async getTodayTasks(householdId: string, userTimezone?: string | null) {
    const todayKey = getTodayKey(userTimezone);
    const { start, end } = dateRangeForDay(todayKey, userTimezone);
    const { data, error } = await supabase
      .from('tasks')
      .select(TASK_SELECT)
      .eq('household_id', householdId)
      .or(
        `and(due_date.gte.${start},due_date.lte.${end}),and(due_date.lt.${start},status.neq.DONE),and(due_date.is.null,status.neq.DONE)`,
      )
      .order('due_date', { ascending: true });
    if (error) throw error;
    return data ?? [];
  },

  async getTodayEvents(householdId: string, userTimezone?: string | null) {
    const todayKey = getTodayKey(userTimezone);
    const { start, end } = dateRangeForDay(todayKey, userTimezone);
    const { data, error } = await supabase
      .from('events')
      .select(EVENT_SELECT)
      .eq('household_id', householdId)
      .gte('start_date', start)
      .lte('start_date', end)
      .order('start_date', { ascending: true });
    if (error) throw error;
    return data ?? [];
  },

  /**
   * Write an explicit status. Completion and its Undo (YAR-476) use this
   * rather than `toggleTaskStatus`, because a toggle can only ever flip back
   * to TODO — an IN_PROGRESS task that was completed and undone came back as
   * TODO. The caller remembers the status it replaced and writes it back.
   * `completed_at` is maintained by the `trg_tasks_completed_at` trigger, and
   * leaving DONE fires `notify_task_restored` (Activity T13).
   */
  async setTaskStatus(taskId: string, status: Enums<'task_status'>) {
    const { error } = await supabase
      .from('tasks')
      .update({ status })
      .eq('id', taskId);
    if (error) throw error;
    return status;
  },

  async toggleTaskStatus(taskId: string, currentStatus: string) {
    const newStatus = currentStatus === 'DONE' ? 'TODO' : 'DONE';
    const { error } = await supabase
      .from('tasks')
      .update({ status: newStatus })
      .eq('id', taskId);
    if (error) throw error;
    return newStatus;
  },

  async getTasksForRange(
    householdId: string,
    startDate: string,
    endDate: string,
    userTimezone?: string | null,
  ) {
    const rangeStart = dateRangeForDay(startDate, userTimezone).start;
    const rangeEnd = dateRangeForDay(endDate, userTimezone).end;
    const { data, error } = await supabase
      .from('tasks')
      .select(TASK_SELECT)
      .eq('household_id', householdId)
      .gte('due_date', rangeStart)
      .lte('due_date', rangeEnd)
      .order('due_date', { ascending: true });
    if (error) throw error;
    return data ?? [];
  },

  async getEventsForRange(
    householdId: string,
    startDate: string,
    endDate: string,
    userTimezone?: string | null,
  ) {
    const rangeStart = dateRangeForDay(startDate, userTimezone).start;
    const rangeEnd = dateRangeForDay(endDate, userTimezone).end;
    const { data, error } = await supabase
      .from('events')
      .select(EVENT_SELECT)
      .eq('household_id', householdId)
      .gte('start_date', rangeStart)
      .lte('start_date', rangeEnd)
      .order('start_date', { ascending: true });
    if (error) throw error;
    return data ?? [];
  },

  /**
   * Every event that has not finished yet — the pool behind the "All ongoing"
   * section and the pinned span bars (YAR-419).
   *
   * Deliberately UNBOUNDED at the top: the section has no distance cap, so a
   * registration window opening in June must come back in November. Every
   * other event read here filters on `start_date`, which is exactly why a
   * multi-day event that began before the browsed window was never fetched at
   * all — the range queries could not see it, so no amount of rendering work
   * would have surfaced it.
   *
   * The "is it multi-day?" test stays client-side (`buildOngoingEvents`):
   * PostgREST cannot compare two columns in a filter, and expressing
   * `end_date > start_date` server-side would need an RPC or a generated
   * column. The cost is over-fetching single-day future events, which is
   * bounded by the household's own calendar.
   */
  async getOngoingEvents(
    householdId: string,
    todayKey: string,
    userTimezone?: string | null,
  ) {
    const todayStart = dateRangeForDay(todayKey, userTimezone).start;
    const { data, error } = await supabase
      .from('events')
      .select(EVENT_SELECT)
      .eq('household_id', householdId)
      .not('end_date', 'is', null)
      .gte('end_date', todayStart)
      .order('start_date', { ascending: true });
    if (error) throw error;
    return data ?? [];
  },

  async getHistoryTasks(
    householdId: string,
    fromKey: string,
    toKey: string,
    userTimezone?: string | null,
  ) {
    const start = dateRangeForDay(fromKey, userTimezone).start;
    const end = dateRangeForDay(toKey, userTimezone).end;
    const { data, error } = await supabase
      .from('tasks')
      .select(TASK_SELECT)
      .eq('household_id', householdId)
      .in('status', ['DONE', 'CANCELLED'])
      .gte('completed_at', start)
      .lte('completed_at', end)
      .order('completed_at', { ascending: false });
    if (error) throw error;
    return data ?? [];
  },

  async getHistoryEvents(
    householdId: string,
    fromKey: string,
    toKey: string,
    userTimezone?: string | null,
  ) {
    const start = dateRangeForDay(fromKey, userTimezone).start;
    const todayStart = dateRangeForDay(toKey, userTimezone).start;
    const { data, error } = await supabase
      .from('events')
      .select(EVENT_SELECT)
      .eq('household_id', householdId)
      // History files a multi-day event under the day it ENDED (YAR-176
      // AC 4), so the window has to be measured on the end.
      //
      // Windowing `start_date` got the rule wrong at both ends: an event that
      // began three days ago and ends tomorrow ALREADY appeared in History,
      // while one that ran six weeks and finished yesterday fell outside the
      // 30-day window and never appeared at all.
      //
      // `end_date` is nullable — single-day events routinely have none — so
      // this cannot be a column swap. The second branch catches those rows on
      // their start, which for them IS the end.
      .or(
        `and(end_date.not.is.null,end_date.gte.${start},end_date.lt.${todayStart}),and(end_date.is.null,start_date.gte.${start},start_date.lt.${todayStart})`,
      )
      .order('start_date', { ascending: false });
    if (error) throw error;
    return data ?? [];
  },

  async getTasksByRelatedEventIds(householdId: string, eventIds: string[]) {
    if (eventIds.length === 0) return [];
    const { data, error } = await supabase
      .from('tasks')
      .select(TASK_SELECT)
      .eq('household_id', householdId)
      .in('related_event_id', eventIds);
    if (error) throw error;
    return data ?? [];
  },

  async getTaskById(taskId: string) {
    const { data, error } = await supabase
      .from('tasks')
      .select(TASK_SELECT)
      .eq('id', taskId)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  async getEventById(eventId: string) {
    const { data, error } = await supabase
      .from('events')
      .select(EVENT_SELECT)
      .eq('id', eventId)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  async createTask(task: {
    title: string;
    due_date?: string | null;
    end_date?: string | null;
    notes?: string | null;
    location?: string | null;
    link?: string | null;
    links?: LinkEntry[];
    child_ids?: string[];
    member_user_ids?: string[];
    /**
     * The event this task hangs off, i.e. ItemFormModal's "Related Event"
     * (YAR-309). Omitting it from the insert is what made the field look
     * like it saved and then quietly forget — see the note on the column
     * below.
     */
    related_event_id?: string | null;
    household_id: string;
  }) {
    const childIds = task.child_ids ?? [];
    const memberIds = task.member_user_ids ?? [];
    const { data, error } = await supabase
      .from('tasks')
      .insert({
        title: task.title,
        due_date: task.due_date ?? null,
        end_date: task.end_date ?? null,
        notes: task.notes ?? null,
        location: task.location ?? null,
        links: task.links ?? [],
        link: scalarFromLinks(task.links ?? []) ?? task.link ?? null,
        // Keep legacy child_id populated with the first pick for backwards
        // compatibility with callers that still read the scalar column.
        child_id: childIds[0] ?? null,
        // The task↔event link. `tasks.related_event_id` has existed since
        // 20260421000001 and every reader uses it — subtask grouping on
        // Schedule/Today/History, the Related Event row in Task Details —
        // but nothing outside approve_email ever WROTE it. So the dropdown
        // took the pick, the form emitted it, and the insert dropped it on
        // the floor: the task saved, and the event never listed it as a
        // sub-task.
        related_event_id: task.related_event_id ?? null,
        household_id: task.household_id,
        status: 'TODO',
      })
      .select('id')
      .single();
    if (error) throw error;
    await replaceTaskChildren(data.id, childIds);
    if (memberIds.length > 0) await replaceTaskMembers(data.id, memberIds);
    return data.id;
  },

  async createEvent(event: {
    title: string;
    start_date: string;
    end_date?: string | null;
    location?: string | null;
    link?: string | null;
    links?: LinkEntry[];
    notes?: string | null;
    child_ids?: string[];
    member_user_ids?: string[];
    household_id: string;
  }) {
    const childIds = event.child_ids ?? [];
    const memberIds = event.member_user_ids ?? [];
    const { data, error } = await supabase
      .from('events')
      .insert({
        title: event.title,
        start_date: event.start_date,
        end_date: event.end_date ?? null,
        location: event.location ?? null,
        links: event.links ?? [],
        link: scalarFromLinks(event.links ?? []) ?? event.link ?? null,
        notes: event.notes ?? null,
        child_id: childIds[0] ?? null,
        household_id: event.household_id,
      })
      .select('id')
      .single();
    if (error) throw error;
    await replaceEventChildren(data.id, childIds);
    if (memberIds.length > 0) await replaceEventMembers(data.id, memberIds);
    return data.id;
  },

  async updateTaskChildren(taskId: string, childIds: string[]) {
    await replaceTaskChildren(taskId, childIds);
    // Keep legacy scalar in sync with first child.
    const { error } = await supabase
      .from('tasks')
      .update({ child_id: childIds[0] ?? null })
      .eq('id', taskId);
    if (error) throw error;
  },

  async updateEventChildren(eventId: string, childIds: string[]) {
    await replaceEventChildren(eventId, childIds);
    const { error } = await supabase
      .from('events')
      .update({ child_id: childIds[0] ?? null })
      .eq('id', eventId);
    if (error) throw error;
  },

  async updateTask(
    taskId: string,
    fields: {
      title?: string;
      due_date?: string | null;
      end_date?: string | null;
      notes?: string | null;
      location?: string | null;
      link?: string | null;
      links?: LinkEntry[];
      child_ids?: string[];
      member_user_ids?: string[];
      /**
       * Pass `null` to unlink. Omit to leave the current link alone — it
       * rides `...rest` into the UPDATE, so an explicitly-passed value is
       * the only thing that changes it.
       */
      related_event_id?: string | null;
    },
  ) {
    const { child_ids, member_user_ids, links, ...rest } = fields;
    const dbFields = {
      ...rest,
      ...(links !== undefined ? { links, link: scalarFromLinks(links) } : {}),
    };
    if (Object.keys(dbFields).length > 0) {
      const { error } = await supabase
        .from('tasks')
        .update(dbFields)
        .eq('id', taskId);
      if (error) throw error;
    }
    if (child_ids) {
      await replaceTaskChildren(taskId, child_ids);
      const { error: legacyErr } = await supabase
        .from('tasks')
        .update({ child_id: child_ids[0] ?? null })
        .eq('id', taskId);
      if (legacyErr) throw legacyErr;
    }
    if (member_user_ids) {
      await replaceTaskMembers(taskId, member_user_ids);
    }
  },

  async updateEvent(
    eventId: string,
    fields: {
      title?: string;
      start_date?: string;
      end_date?: string | null;
      location?: string | null;
      link?: string | null;
      links?: LinkEntry[];
      notes?: string | null;
      child_ids?: string[];
      member_user_ids?: string[];
    },
  ) {
    const { child_ids, member_user_ids, links, ...rest } = fields;
    const dbFields = {
      ...rest,
      ...(links !== undefined ? { links, link: scalarFromLinks(links) } : {}),
    };
    if (Object.keys(dbFields).length > 0) {
      const { error } = await supabase
        .from('events')
        .update(dbFields)
        .eq('id', eventId);
      if (error) throw error;
    }
    if (child_ids) {
      await replaceEventChildren(eventId, child_ids);
      const { error: legacyErr } = await supabase
        .from('events')
        .update({ child_id: child_ids[0] ?? null })
        .eq('id', eventId);
      if (legacyErr) throw legacyErr;
    }
    if (member_user_ids) {
      await replaceEventMembers(eventId, member_user_ids);
    }
  },

  async deleteTask(taskId: string): Promise<void> {
    // task_children is ON DELETE CASCADE, so the join rows go with it.
    const { error } = await supabase.from('tasks').delete().eq('id', taskId);
    if (error) throw error;
  },

  async deleteEvent(eventId: string): Promise<void> {
    const { error } = await supabase.from('events').delete().eq('id', eventId);
    if (error) throw error;
  },

  /**
   * How many sessions "Delete this and following" would remove (YAR-443):
   * this occurrence and every later one in the same series. Same filter as
   * the delete below, so the number in the confirmation is the number that
   * goes.
   */
  async countThisAndFollowing(
    householdId: string,
    seriesId: string,
    occurrenceStart: string,
  ): Promise<number> {
    const { count, error } = await supabase
      .from('events')
      .select('id', { count: 'exact', head: true })
      .eq('series_id', seriesId)
      .gte('start_date', occurrenceStart)
      .eq('household_id', householdId);
    if (error) throw error;
    return count ?? 0;
  },

  /**
   * "Delete this and following" (YAR-443): removes this occurrence and every
   * later one in its series. One RLS-bound statement — managers only, like
   * the single delete — with the household bound explicitly, since RLS alone
   * scopes to EVERY household the caller belongs to. Earlier sessions stay.
   * Returns how many rows went.
   *
   * RLS lets only PRIMARY / CO_ADMIN delete; for anyone else PostgREST does
   * not error, it deletes 0 rows. The occurrence being deleted always matches
   * its own filter, so 0 rows is never success — it throws.
   */
  async deleteThisAndFollowing(
    householdId: string,
    seriesId: string,
    occurrenceStart: string,
  ): Promise<number> {
    const { count, error } = await supabase
      .from('events')
      .delete({ count: 'exact' })
      .eq('series_id', seriesId)
      .gte('start_date', occurrenceStart)
      .eq('household_id', householdId);
    if (error) throw error;
    if (!count) throw new SeriesDeleteNothingDeletedError();
    return count;
  },

  /**
   * How many sessions "All events" would remove: every occurrence of the
   * series, past ones included. Same filter as the delete below.
   */
  async countWholeSeries(
    householdId: string,
    seriesId: string,
  ): Promise<number> {
    const { count, error } = await supabase
      .from('events')
      .select('id', { count: 'exact', head: true })
      .eq('series_id', seriesId)
      .eq('household_id', householdId);
    if (error) throw error;
    return count ?? 0;
  },

  /**
   * "All events": removes every occurrence of the series, past ones
   * included. Same guards as `deleteThisAndFollowing` — one RLS-bound
   * statement, household bound explicitly, and 0 rows (RLS refused, or the
   * series is already gone) throws rather than reading as success.
   */
  async deleteWholeSeries(
    householdId: string,
    seriesId: string,
  ): Promise<number> {
    const { count, error } = await supabase
      .from('events')
      .delete({ count: 'exact' })
      .eq('series_id', seriesId)
      .eq('household_id', householdId);
    if (error) throw error;
    if (!count) throw new SeriesDeleteNothingDeletedError();
    return count;
  },
};
